import { useCallback, useEffect, useMemo, useState } from 'react'
import { listMarkups, createMarkup, updateMarkup, deleteMarkup } from '../services/markups'

let tempSeq = 0
const tempId = () => `tmp-${Date.now()}-${(tempSeq += 1)}`

// Loads the current user's markups for one standard, indexes them by page, and
// exposes optimistic add/update/remove. Mutations commit immediately so an
// in-progress shape isn't lost when windowing unmounts an off-screen page.
export function useMarkups(standard) {
  const [markups, setMarkups] = useState([])
  const [status, setStatus] = useState(standard ? 'loading' : 'idle')

  useEffect(() => {
    if (!standard) {
      setMarkups([])
      setStatus('idle')
      return undefined
    }
    let alive = true
    setStatus('loading')
    listMarkups(standard)
      .then((list) => {
        if (!alive) return
        setMarkups(list)
        setStatus('ready')
      })
      .catch(() => {
        if (alive) setStatus('error')
      })
    return () => {
      alive = false
    }
  }, [standard])

  const byPage = useMemo(() => {
    const map = {}
    for (const m of markups) {
      const p = m.page
      if (!map[p]) map[p] = []
      map[p].push(m)
    }
    return map
  }, [markups])

  const add = useCallback(
    async (payload) => {
      const id = tempId()
      const optimistic = { _id: id, standard, text: '', style: {}, ...payload }
      setMarkups((prev) => [...prev, optimistic])
      try {
        const created = await createMarkup({ ...payload, standard })
        let local
        setMarkups((prev) => {
          const current = prev.find((m) => m._id === id)
          local = current
          const merged = current
            ? { ...created, geometry: current.geometry, text: current.text, style: current.style }
            : created
          return prev.map((m) => (m._id === id ? merged : m))
        })
        if (
          local &&
          (JSON.stringify(local.geometry) !== JSON.stringify(created.geometry) ||
            local.text !== created.text)
        ) {
          const flushed = await updateMarkup(created._id, {
            geometry: local.geometry,
            text: local.text,
            style: local.style,
          })
          setMarkups((prev) => prev.map((m) => (m._id === created._id ? flushed : m)))
          return flushed
        }
        return created
      } catch (err) {
        setMarkups((prev) => prev.filter((m) => m._id !== id))
        throw err
      }
    },
    [standard]
  )

  const update = useCallback(async (id, changes) => {
    let previous
    setMarkups((prev) => {
      previous = prev.find((m) => m._id === id)
      return prev.map((m) => (m._id === id ? { ...m, ...changes } : m))
    })
    if (String(id).startsWith('tmp-')) return previous ? { ...previous, ...changes } : null
    try {
      const updated = await updateMarkup(id, changes)
      setMarkups((prev) => prev.map((m) => (m._id === id ? updated : m)))
      return updated
    } catch (err) {
      if (previous) {
        setMarkups((prev) => prev.map((m) => (m._id === id ? previous : m)))
      }
      throw err
    }
  }, [])

  const remove = useCallback(async (id) => {
    let snapshot
    setMarkups((prev) => {
      snapshot = prev
      return prev.filter((m) => m._id !== id)
    })
    if (String(id).startsWith('tmp-')) return
    try {
      await deleteMarkup(id)
    } catch (err) {
      setMarkups(snapshot)
      throw err
    }
  }, [])

  return { markups, byPage, status, add, update, remove }
}
